import React, { useCallback, useEffect, useState } from 'react';
import { NoteContent } from 'components/HealthRecords/AppointmentNotes/AppointmentNoteDetails';
import Icon from 'components/Utils/NewIcon/index';
import MiscUtils from 'services/MiscUtils';
import ProgressMessage from 'components/Utils/ProgressMessage';
import './style.scss';
import CPPUtils from 'services/CPPUtils';
import DefaultModal from 'components/Utils/DefaultModal';
import DocumentPreview from 'components/Utils/DocumentPreview';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import { DefaultSwitch } from 'services/UIUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { PERMISSIONS } from 'services/StaffUtils';
import { ActivePhysician } from 'careand-redux/selectors';
import PerformanceUtils from 'services/PerformanceUtils';
import ConfirmationIcon from 'components/Utils/NewIcon/ConfirmationIcon';
import { setFormValues } from 'hooks/useReduxForm';

const ACTIVE_TAB = 'note';

interface CPPTimelineAppointmentNoteProps {
  note: any;
  patient: any;
  setActiveCPPTab: (tab: string) => void;
  items?: React.ReactNode;
}

const CPPTimelineAppointmentNote = ({
  note,
  patient,
  setActiveCPPTab,
  ...props
}: CPPTimelineAppointmentNoteProps) => {
  const dispatch = useAppDispatch();
  const physicians = useAppSelector((state) => state.physicians.byId);
  const authentication = useAppSelector((state) => state.authentication);

  const ownPhysician = useAppSelector(ActivePhysician);
  const author = physicians[note.author_id];
  const isAuthor = note.author_id === ownPhysician!.id;

  const isIntern = ownPhysician!.intern_of;
  const isAuthorAnIntern = !!author?.intern_of;

  const isAuthorized = isAuthor || (!isIntern && isAuthorAnIntern);
  const allowDelete = isAuthorized || authentication.permissions.includes(PERMISSIONS.super_admin);

  const formName = CPPUtils.getFormName(ACTIVE_TAB, patient.id);

  const editCPPItem = useCallback(
    (event?: React.MouseEvent, additionalData: any = {}) => {
      if (event && event.preventDefault) event.preventDefault();
      let data = CPPUtils.getAppointmentNoteEditElement(note);
      data = { ...data, ...additionalData };
      dispatch(setFormValues({ formName, values: { ...data } }));
      setActiveCPPTab(ACTIVE_TAB);
    },
    [note, formName, dispatch, setActiveCPPTab],
  );
  async function deleteNote() {
    await dispatch(api.HealthRecords.deleteNote({ id: note.id }));
    await dispatch(api.HealthRecords.getCPP({ patientId: patient.id }));
  }

  return (
    <>
      {note.item_type === 'text' && (
        <TextNote
          note={note}
          isAuthorized={isAuthorized}
          editCPPItem={() => editCPPItem()}
          physicians={physicians}
          allowDelete={allowDelete}
          deleteNote={deleteNote}
          items={props.items}
        />
      )}
      {note.item_type === 'attachment' && (
        <>
          <AttachmentNote
            patient={patient}
            note={note}
            isAuthorized={isAuthorized}
            editCPPItem={editCPPItem}
          />
          {props.items}
        </>
      )}
    </>
  );
};

const TextNote = React.memo(
  ({ note, isAuthorized, editCPPItem, physicians, allowDelete, deleteNote, items }: any) => (
    <div className="timeline-note">
      <div className={getRootClassName(note)}>
        <NoteContent
          physicians={physicians}
          note={note}
          showInfoText={false}
          items={items}
          hoverIcons={
            <HoverIcons
              className="timeline-note-hover-icons"
              isAuthorized={isAuthorized}
              editCPPItem={editCPPItem}
              allowDelete={allowDelete}
              deleteNote={deleteNote}
            />
          }
        />
        {note.value.notes !== undefined && <span>Notes: {note.value.notes}</span>}
      </div>
    </div>
  ),
);

const AttachmentNote = React.memo(({ note }: any) => {
  const [openPreview, setOpenPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | undefined>();

  const dispatch = useAppDispatch();

  async function getAttachment() {
    if (loading) return;
    if (fileUrl) {
      setOpenPreview(true);
    } else {
      setLoading(true);
      let result = await dispatch(
        api.HealthRecords.getNotesAttachment({ appointmentNoteId: note.id }),
        {
          errorTitle: 'Note Attachment',
        },
      );
      setLoading(false);
      if (result) {
        let url = MiscUtils.getBlobUrl(result);
        setFileUrl(url);
        setOpenPreview(true);
      }
    }
  }

  return (
    <>
      {openPreview && (
        <DefaultModal
          className="large-modal"
          key="preview-document-modal"
          wrap={true}
          padding={false}
          title="Note Attachment"
          content={<EditNoteAttachment url={fileUrl} note={note} />}
          isOpen={openPreview}
          onRequestClose={() => setOpenPreview(false)}
        />
      )}
      <div
        className="item-wrapper item-note_attachment clickable timeline-note"
        onClick={getAttachment}
      >
        <div className={getRootClassName(note)}>
          <div className="top-bar">
            <Icon src="memo-pad" height="15px" />
            <span className="small-title">Note Attachment</span>
          </div>
          <div className="item-content">
            {note.value.notes !== undefined && (
              <span className="inline-block">Notes: {note.value.notes}</span>
            )}
          </div>
        </div>
        <div className="progress-wrapper">
          <ProgressMessage show={loading} />
        </div>
      </div>
    </>
  );
});

const EditNoteAttachment = ({ note, url, type }: { note: any; url?: string; type?: string }) => {
  const dispatch = useAppDispatch();
  const [notes, setNotes] = useState(note.value.notes);
  const [hide, setHide] = useState(note.value.hide);
  const [loading, setLoading] = useState(false);

  const hasChanged = notes !== note.value.notes || hide !== note.value.hide;

  async function saveChanges() {
    let newNote = PerformanceUtils.clone(note);
    newNote.value.notes = notes;
    newNote.value.hide = hide;
    setLoading(true);

    await dispatch(api.HealthRecords.editNote({ id: newNote.id, appointmentNote: newNote }), {
      errorTitle: 'Edit Note Attachment',
    });

    setLoading(false);
  }

  return (
    <div className="edit-note-attachment full-height">
      <div className="underside-right-panel full-height-panel scroll-list padded-box">
        <span className="small-title">Notes</span>
        <TextareaAutosize
          name="notes"
          value={notes}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(event.target.value)}
        />
        <DefaultSwitch
          name="hide"
          onChange={(event: any, isInputChecked: boolean) => setHide(isInputChecked)}
          label="Hide"
          padded={false}
          checked={hide || false}
          className="red"
        />
        <DefaultButton
          name="Save Changes"
          onClick={saveChanges}
          style={{ width: '100%' }}
          loading={loading}
          disabled={!hasChanged}
        />
      </div>
      <div className="preview full-height">
        <DocumentPreview url={url} type={type} />
      </div>
    </div>
  );
};

const HoverIcons = React.memo(
  ({ isAuthorized, editCPPItem, allowDelete, deleteNote, className }: any) => (
    <div className={`hover-icons hover-edit-icon ${className || ''}`}>
      {isAuthorized && (
        <Icon
          src="pencil"
          width="20px"
          alt="Edit"
          className="icon-edit"
          showTooltip={true}
          onClick={editCPPItem}
        />
      )}
      {allowDelete ? (
        <ConfirmationIcon
          confirmText="Are you sure you want to delete this note?"
          src="trash"
          width="20px"
          alt="Delete"
          className="icon-trash"
          showTooltip={true}
          onClick={deleteNote}
        />
      ) : null}
    </div>
  ),
);

function getRootClassName(note: any) {
  return note.value.hide ? 'greyed-out' : 'visible';
}

export default React.memo(CPPTimelineAppointmentNote);
